<template>
  <cs-preloader-overlay :loading="loading">
    <div v-if="Object.keys(PROJECT).length > 0">
      <div id="aperture"></div>

      <div id="warp" class="wide">
        <div
          class="wrap-container"
          :class="{ 'full-size': PROJECT.bill_image_link && isDeviceSize.laptop }"
        >
          <div class="project-header-container">
            <div :class="$style.header">
              <project-header
                :project="PROJECT"
                :language="getLanguage"
              />
            </div>
          </div>

          <div v-if="PROJECT.bill_image_link" class="side wrap-box">
            <div id="previewScrollable" class="preview" @click="showPreview">
              <img
                :src="PROJECT.bill_image_link"
                class="preview-img"
                alt=""
              />
            </div>

            <payment-description
              v-if="!isVisibleDescription"
              :description="billDescription"
              :tilda-parameters="tildaParams"
            />
          </div>

          <div class="container wrap-box">
            <payments-disabled
              v-if="!PROJECT.paymentsEnabled || !PROJECT.validated"
            />

            <payment-description
              v-if="isVisibleDescription"
              :description="billDescription"
              :tilda-parameters="tildaParams"
            />

            <div v-if="isShownTariffList" v-m-b="10" class="tariff-list">
              <selectable-tariff
                v-for="tariff in availableBillTariffs"
                :key="tariff.id"
                :tariff="tariff"
                :payments-enabled="PROJECT.paymentsEnabled"
                @click="onSelectTariff(tariff)"
              />
            </div>

            <template v-else-if="showField('rating')">
              <div v-m-b="10">
                <grade-management
                  ref="rating"
                  v-model="rating"
                  :rating="PROJECT.senderFields.rating"
                />
              </div>
            </template>

            <template v-if="isShownTipsManagement">
              <div v-m-b="10">
                <tips-management
                  :bill-amount="billTariff.amount"
                  :currency="billTariff.currency"
                  :tips-amount="formattedAmount"
                  :tips-list="PROJECT.tips"
                  @change="handleChangeTipsAmount"
                />
              </div>
            </template>

            <div v-if="!isCasual && !isTips && isShownPaymentForm" class="tariff-selected">
              <applicable-tariff
                v-if="isShownTariffVariant"
                v-m-b="10"
                :tariff="tariff"
                :payments-enabled="PROJECT.paymentsEnabled"
                :no-interactive="isSingleTariff"
                @click="onResetTariff"
              >
                <div v-if="tariff.arrangement" slot="additional" @click.stop>
                  <ticket-counter
                    :arrangement="tariff.arrangement"
                    @update-count="handleUpdateCount"
                  />
                </div>
              </applicable-tariff>
              <write-off-options
                v-if="isShownWriteOffOptions"
                :tariff="tariff"
                @select="onSelectWriteOffOption"
              />
            </div>

            <form v-show="isShownPaymentForm">
              <div
                v-show="senderFieldList.length || isShownPromoCode"
                class="special-fields"
                :class="{ 'full-rounded': !isShownAmount }"
              >
                <InputText
                  v-if="showField('name')"
                  v-model="senderFields.name"
                  ref="name"
                  class="focusElements"
                  inputId="name"
                  iconClass="icon-user"
                  :inputTitle="$t('enter.name')"
                  :input-placeholder="$t('enter.name')"
                  :maxLength="50"
                />

                <InputEmail
                  v-show="showField('email')"
                  v-model="senderFields.email"
                  ref="email"
                  inputId="email"
                  class="focusElements"
                  iconClass="icon-mail"
                  :inputTitle="$t('enter.email')"
                  :input-placeholder="$t('enter.email')"
                  required
                />

                <InputPhone
                  v-show="showField('phone')"
                  v-model="senderFields.phone"
                  ref="phone"
                  inputId="phone"
                  class="focusElements"
                  iconClass="icon-phone"
                  :inputTitle="$t('enter.phone')"
                  :input-placeholder="$t('enter.phone')"
                  required
                />

                <InputTextarea
                  v-if="showField('comment')"
                  v-model="senderFields.comment"
                  ref="comment"
                  inputId="comment"
                  class="focusElements"
                  iconClass="icon-message"
                  :inputTitle="$t('enter.comment')"
                  :input-placeholder="$t('enter.comment')"
                />

                <InputTextarea
                  v-if="showField('message')"
                  v-model="senderFields.message"
                  ref="message"
                  class="focusElements"
                  inputId="message"
                  iconClass="icon-receipt"
                  :inputTitle="$t('enter.message')"
                  :input-placeholder="$t('enter.message')"
                />

                <InputTextarea
                  v-if="showField('address')"
                  v-model="senderFields.address"
                  ref="address"
                  class="focusElements"
                  inputId="address"
                  iconClass="icon-map-pin"
                  :inputTitle="$t('enter.address')"
                  :input-placeholder="$t('enter.address')"
                />

                <div v-if="isShownPromoCode">
                  <input-text
                    v-model="promoCodeCoupon"
                    ref="promoCode"
                    :error="isPromoCodeInvalid"
                    class="focusElements"
                    :class="{ icons: promoCodeCompleted || isPromoCodeInvalid }"
                    inputId="promoCode"
                    iconClass="icon-coupon"
                    :inputTitle="$t('enter.promocode')"
                    :input-placeholder="$t('enter.promocode')"
                  />
                  <div v-if="promoCodeCompleted" class="icon-coupon-completed"></div>
                  <div
                    v-else-if="isPromoCodeInvalid"
                    class="icon-coupon-invalid"
                    @click="promoCodeCoupon = ''"
                  ></div>
                </div>
              </div>

              <div
                class="payment-amount"
                :class="{ 'single-block': isSingleBlockView }"
              >
                <InputPrice
                  v-show="isShownAmount"
                  ref="amount"
                  v-model.lazy="amount"
                  class="focusElements"
                  inputId="amount"
                  :input-name="displayAmountPlaceholder"
                  :input-title="displayAmountPlaceholder"
                  :min-value="minLimitAmount"
                  :max-value="maxLimitAmount"
                  :readonly="readonlyAmount"
                  :currency="PROJECT.currency"
                  required
                  @change="amount = $event"
                />
                <span v-if="promoCodeCompleted" class="amount-before-promocode">
                  {{ displayAmountWithoutPromocode }}
                </span>

                <div v-if="isShownComissionInfo" class="payment-comission">
                  <span>{{ $t('comission.none') }}</span>
                </div>

                <div v-if="isShownPaymentButtons" class="payment-buttons">
                  <button
                    v-if="isCardPaymentAvailable"
                    data-test="billPaymentCardPaymentButton"
                    :class="{
                      'payment-button': true,
                      'loading': loading,
                      'disabled': !isValid,
                    }"
                    @click.prevent="goPay($event, null)"
                    v-text="displayAmountText"
                  ></button>

                  <div v-if="isShownYandexPayment" class="yandex-payment-wrapper payment-button">
                    <YandexPay
                      data-test="billPaymentYandexPayButton"
                      :merchantName="PROJECT.project_name"
                      :amount="amount"
                      :disabled="!isValid"
                      :requestData="getPaymentData"
                      @loading="handleLoading"
                      @click-on-disabled="validateForm"
                    />
                  </div>

                  <div v-if="isShownApplePayment" class="apple-payment-wrapper payment-button">
                    <ApplePay
                      data-test="billPaymentApplePayButton"
                      :label="PROJECT.project_name"
                      :amount="amount"
                      :disabled="!isValid"
                      :requestData="getPaymentData"
                      @loading="handleLoading"
                      @click-on-disabled="validateForm"
                    />
                  </div>

                  <div v-if="isShownGooglePayment" class="google-payment-wrapper payment-button">
                    <GooglePay
                      data-test="billPaymentGooglePayButton"
                      :label="PROJECT.project_name"
                      :amount="amount"
                      :disabled="!isValid"
                      :requestData="getPaymentData"
                      @loading="handleLoading"
                      @click-on-disabled="validateForm"
                    />
                  </div>

                  <button
                    v-if="isQiwiPaymentAvailable"
                    data-test="billPaymentQiwiButton"
                    class="qiwi-payment-button payment-button"
                    :class="{
                      'loading': loading,
                      'disabled': !isValid,
                    }"
                    @click.prevent="goPay($event, 'QIWI')"
                  >
                    <img src="~@/assets/svg/qiwi.svg" alt="">
                    <span>Qiwi</span>
                  </button>

                  <button
                    v-if="isYooMoneyPaymentAvailable"
                    data-test="billPaymentYoomoneyButton"
                    class="yoomoney-payment-button payment-button"
                    :class="{
                      'loading': loading,
                      'disabled': !isValid,
                    }"
                    @click.prevent="goPay($event, 'YOOMONEY')"
                  >
                    <img src="~@/assets/svg/yoomoney.svg" alt="">
                    <span>{{ $t('common.yoomoney') }}</span>
                  </button>

                  <template v-if="hasWidgetYooMoney">
                    <div id="payment-form" ref="externalCheckoutWidget" style="padding: 0;"></div>
                  </template>

                  <button
                    v-if="isPayMePaymentAvailable"
                    data-test="billPaymentPayMeButton"
                    class="payme-payment-button payment-button"
                    :class="{
                      'loading': loading,
                      'disabled': !isValid,
                    }"
                    :disabled="!isValid"
                    @click.prevent="goPay($event, 'PAYME')"
                  >
                    <img src="~@/assets/svg/payme.svg" alt="">
                  </button>
                </div>

                <div v-if="PROJECT.selfEmployed && !isCurrencyCrypto" class="self-employed-wrapper">
                  <PaymentSelfEmployed />
                </div>
              </div>

              <div class="policy-privacy" v-html="displayPolicyPrivacy"></div>
            </form>

            <FooterSection />
          </div>
        </div>

        <unsafe-deal-info-dialog
          v-if="PROJECT.safeDeal"
          v-model="isShownUnsafeDealInfo"
          @submit="handleSubmitUnsafeDeal"
        />
      </div>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { isSafari, isAndroid } from 'mobile-device-detect';

import { formatAmount } from 'lib/utils/amount';
import { calcPercent } from 'lib/utils/number';
import throttle from 'lib/utils/throttle';

// import logrocket from '@/services/logrocket';
import HTTPService from '@/services/Http';

import ProjectHeader from 'organisms/project/ProjectHeader';
import PaymentDescription from '@/components/pages/PaymentDescription';
import PaymentSelfEmployed from '@/components/pages/PaymentSelfEmployed';
import PaymentsDisabled from '@/components/pages/PaymentsDisabled';

import ApplePay from '@/modules/payments/ApplePay';
import GooglePay from '@/modules/payments/GooglePay';
import YandexPay from '@/modules/payments/YandexPay';

import SelectableTariff from 'organisms/tariff/SelectableTariff';
import ApplicableTariff from 'organisms/tariff/ApplicableTariff';
import TicketCounter from 'organisms/tariff/TicketCounter';

import UnsafeDealInfoDialog from 'organisms/payment/components/UnsafeDealInfoDialog';
import GradeManagement from 'organisms/grade/GradeManagement';
import TipsManagement from 'organisms/tips/TipsManagement';

import WriteOffOptions from './subscription/components/WriteOffOptions';

import { DAILY, SELECTIVELY } from './subscription/const';

import { MERCHANT_ID } from '@/.env';

const SLICE_LENGTH = 100;

const REQUIRED_FIELDS = ['phone', 'email'];
const UNIQUE_FIELDS = ['address', 'comment', 'message'];
const NON_VALIDATED_FIELDS = ['rating'];

const UNSAFE_PAYMENT_TYPES = ['QIWI', 'YOOMONEY', 'PAYME'];

export default {
  name: 'BillAmount',
  components: {
    ProjectHeader,
    PaymentDescription,
    PaymentSelfEmployed,
    PaymentsDisabled,
    ApplePay,
    GooglePay,
    YandexPay,
    WriteOffOptions,
    SelectableTariff,
    ApplicableTariff,
    TicketCounter,
    UnsafeDealInfoDialog,
    GradeManagement,
    TipsManagement,
  },
  async created() {
    this.SET_TARIFF(null);
    this.SET_WRITE_OFF_OPTION(null);

    await this.loadBill();

    this.initializeApplePayment();
    this.initializeGooglePayment();
    this.initializeYandexPayment();
  },
  mounted() {
    this.setFocusToAvailableInputField();
  },
  data() {
    return {
      loading: false,

      amount: '',
      readonlyAmount: false,

      commission: 0,
      slicedDescription: true,

      senderFields: {
        address: '',
        comment: '',
        email: '',
        message: '',
        name: '',
        phone: '',
      },

      isApplePayAvailable: false,
      isGooglePayAvailable: false,
      isYandexPayAvailable: false,

      tildaParams: {},

      promoCodeCoupon: '',
      promoCodeCompleted: null,
      isPromoCodeInvalid: false,
      discountAmount: 0,

      checkout: null,
      isShownUnsafeDealInfo: false,
      paymentTypeByUnsafeDeal: null,

      countTickets: 1,
      rating: 0,
    };
  },
  computed: {
    currency() {
      return this.PROJECT.currency;
    },
    displayPolicyPrivacy() {
      return this.$t('policy.privacy.bill', {
        offerta: this.getOfferta,
        policy: this.getPolicyPrivacy,
        agreement: this.getAgreement,
      });
    },
    isSingleBlockView() {
      const senderFieldListWithoutRating = this.senderFieldList.filter(field => field !== 'rating');

      return this.$validate.isEmptyArray(senderFieldListWithoutRating) && !this.isShownPromoCode;
    },
    isCasual() {
      return this.PROJECT.bill_type === 'CASUAL';
    },
    isTips() {
      return this.PROJECT.bill_type === 'TIPS';
    },
    isShownAmount() {
      if (this.isCasual || this.isTips) {
        return true;
      }

      return this.$validate.isNotNull(this.tariff) && (this.$validate.isNull(this.tariff.amount) || this.tariff.promoCodeApplicable);
    },
    showSliceButton() {
      return this.PROJECT.bill_description && this.PROJECT.bill_description.length > SLICE_LENGTH;
    },
    displayToggleText() {
      return this.slicedDescription ? `${this.$t('common.details')}` : `${this.$t('common.hide')}`;
    },
    formattedAmount() {
      const replacedAmount = this.amount.replace(/\s/g, '');
      const preparedAmount = Math.round(replacedAmount * 10 ** this.currency.significant_zeros);

      return this.$validate.isPositiveNumber(preparedAmount) ? preparedAmount : 0;
    },
    isValidSenderFields() {
      if (this.$validate.isEmptyArray(this.senderFieldList)) {
        return false;
      }

      return this.senderFieldList.every(field => {
        if (NON_VALIDATED_FIELDS.includes(field)) {
          return true;
        }

        if (this.$validate.isUndefined(this.$refs[field])) {
          return false;
        }

        if (REQUIRED_FIELDS.some(el => el === field)) {
          return this.senderFields[field].length && this.$refs[field].$v && !this.$refs[field].$v.$error;
        } else {
          return this.$refs[field].$v && !this.$refs[field].$v.$error;
        }
      });
    },
    isValidAmount() {
      if (this.$validate.isEmptyString(this.amount)) {
        return false;
      }

      return this.$validate.isPositiveNumber(this.formattedAmount) && this.$refs.amount && !this.$refs.amount.$v.$error;
    },
    isValid() {
      if (!this.PROJECT.testPayment && !this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isPaymentRestrictions) {
        return false;
      }

      if (this.formattedAmount < this.minLimitAmount || this.formattedAmount > this.maxLimitAmount) {
        return false;
      }

      if (this.$validate.isNotEmptyArray(this.senderFieldList)) {
        return this.isValidAmount && this.isValidSenderFields;
      }

      return this.isValidAmount;
    },
    recurrentPaymentPermission() {
      return this.PROJECT.recurrentPaymentPermission;
    },
    periodicRecurrentPaymentPermission() {
      return this.PROJECT.periodicRecurrentPaymentPermission;
    },
    isPaymentRestrictions() {
      if (!this.isSubscription) {
        return false;
      }

      if (!this.recurrentPaymentPermission && !this.periodicRecurrentPaymentPermission) {
        return true;
      }

      const { per } = this.billTariffSubscription;

      if (!this.recurrentPaymentPermission && this.$validate.isNull(per)) {
        return true;
      }

      if (!this.periodicRecurrentPaymentPermission && this.$validate.isNotNull(per)) {
        return true;
      }

      return false;
    },
    senderFieldList() {
      const { senderFields, sender, safeDeal, bill_type: billType } = this.PROJECT;

      let exist = [];

      for (const field in senderFields) {
        if (senderFields[field].selected) {
          exist.push(field);
        }
      }

      if (this.$validate.isObject(sender)) {
        exist = [...exist, ...Object.keys(sender)];
      }

      if (this.$validate.isNull(this.tariff)) {
        return exist;
      }

      if (this.$validate.isNotNull(this.tariff.contentUrl) && !exist.includes('email')) {
        exist.push('email');
      }

      if (safeDeal && !exist.includes('email')) {
        exist.push('email');
      }

      if (billType === 'TICKET' && !exist.includes('email')) {
        exist.push('email');
      }

      const { subscription } = this.tariff;

      // casual bill
      if (this.$validate.isNull(subscription)) {
        return exist;
      }

      // subscription from api
      if (this.$validate.isNull(subscription.per) && !exist.includes('email')) {
        exist.push('email');
      }

      // one time payment
      if (this.$validate.isNotNull(this.writeOffOption) && !exist.includes('email')) {
        exist.push('email');
      }

      return exist;
    },
    getSenderFieldsForRequest() {
      const senderFieldsCopy = Object.assign({}, this.senderFields);

      for (const key in senderFieldsCopy) {
        if (!senderFieldsCopy[key]) {
          delete senderFieldsCopy[key];
        }
      }

      return senderFieldsCopy;
    },
    isVisibleDescription() {
      let mask = '';
      mask += this.isDeviceSize.mobilePlus ? 1 : 0;
      mask += this.PROJECT.bill_image_link ? 1 : 0;
      mask += this.senderFieldList.length ? 1 : 0;
      mask += this.PROJECT.bill_description ? 1 : 0;

      if (/0111|0101/.test(mask)) {
        return false;
      }

      return true;
    },
    billCode() {
      return this.$route.params.id;
    },
    isShownTariffList() {
      if (this.isCasual || this.isTips) {
        return false;
      }

      return this.$validate.isNull(this.tariff);
    },
    isShownTipsManagement() {
      if (!this.isTips) {
        return false;
      }

      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (!this.$validate.isObjectWithFields(this.billTariff, ['amount'])) {
        return false;
      }

      return this.$validate.isNotNull(this.billTariff.amount);
    },
    isShownPaymentForm() {
      if (this.isCasual) {
        return true;
      }

      return this.$validate.isNotNull(this.tariff);
    },
    isShownWriteOffOptions() {
      if (this.$validate.isNull(this.tariff)) {
        return false;
      }

      const { subscription } = this.tariff;

      if (this.$validate.isNull(subscription)) {
        return false;
      }

      if (subscription.oneTimePayment) {
        return true;
      }

      return [SELECTIVELY].includes(subscription.per);
    },
    displayAmountText() {
      if (this.$validate.isNull(this.tariff)) {
        return '';
      }

      if (this.isTips) {
        return this.$t('common.transfer');
      }

      const { subscription } = this.tariff;
      const amount = formatAmount(this.totalAmount, this.currency);

      // casual bill
      if (this.$validate.isNull(subscription)) {
        return `${this.$t('common.transfer')} ${amount}`;
      }

      // subscription from api
      if (this.$validate.isNull(subscription.per)) {
        return this.$t('common.subscribe');
      }

      // one time payment
      if (this.$validate.isNull(this.writeOffOption)) {
        return `${this.$t('common.pay')} ${amount}`;
      }

      return this.$t('common.subscribe');
    },
    isSubscription() {
      if (this.$validate.isNull(this.tariff)) {
        return false;
      }

      const { subscription } = this.tariff;

      if (this.$validate.isNull(subscription)) {
        return false;
      }

      if (this.$validate.isNull(subscription.per)) {
        return !subscription.oneTimePayment;
      }

      if (this.$validate.isNull(this.writeOffOption)) {
        return false;
      }

      return true;
    },
    isShownApplePayment() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (!this.isApplePayEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isApplePayAvailable;
    },
    isShownGooglePayment() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (!this.isGooglePayEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isGooglePayAvailable;
    },
    isShownYandexPayment() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (!this.isYandexPayEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isYandexPayAvailable;
    },
    isSingleTariff() {
      return this.$validate.isFixedLengthArray(this.availableBillTariffs, 1);
    },
    billTariffSubscription() {
      if (this.$validate.isNull(this.tariff)) {
        return null;
      }

      const { subscription } = this.tariff;

      // casual bill
      if (this.$validate.isNull(subscription)) {
        return null;
      }

      // subscription from api
      if (this.$validate.isNull(subscription.per)) {
        return subscription;
      }

      // one time payment
      if (this.$validate.isNull(this.writeOffOption)) {
        return null;
      }

      return {
        ...subscription,
        per: this.writeOffOption,
      };
    },
    billTariff() {
      if (this.$validate.isNull(this.tariff)) {
        return null;
      }

      return Object.assign({}, {
        ...this.tariff,
        subscription: this.billTariffSubscription,
        arrangement: this.getArrangement,
      });
    },
    getArrangement() {
      if (this.$validate.isNull(this.tariff)) {
        return null;
      }

      const { arrangement } = this.tariff;

      if (this.$validate.isNull(arrangement)) {
        return null;
      }

      return {
        ...arrangement,
        count: this.$validate.isPositiveNumber(arrangement.countBundle)
          ? arrangement.countBundle : this.countTickets,
      };
    },
    getPaymentData() {
      return {
        amount: {
          amount: this.formattedAmount,
          commission: this.commission,
          currency: this.PROJECT.currency.code,
        },
        billId: this.PROJECT.billId,
        projectId: this.PROJECT.project_id,
        sender: this.getSenderFieldsForRequest,
        billTariff: this.billTariff,
        localization: this.getLanguage,
        currency: this.PROJECT.currency,
        rating: this.getRating,
        tips: this.getTips,
      };
    },
    minLimitAmount() {
      if (this.$validate.isNull(this.tariff)) {
        return this.PROJECT.min_amount;
      }

      if (this.isSubscription) {
        if (this.PROJECT.subscriptionTokenBinding) {
          if (this.$validate.isNull(this.tariff.amount)) {
            return this.PROJECT.min_recurrent_amount;
          }

          return this.tariff.amount !== 100 ? this.PROJECT.min_recurrent_amount : 0;
        }

        return this.PROJECT.min_recurrent_amount;
      }

      return this.PROJECT.min_amount;
    },
    maxLimitAmount() {
      if (this.isSubscription) {
        return this.PROJECT.max_recurrent_amount;
      }

      return this.PROJECT.max_amount;
    },
    billDescription() {
      const description = this.PROJECT.bill_description;

      if (this.$validate.isNull(description)) {
        return '';
      }

      return description;
    },
    isShownTariffVariant() {
      if (this.$validate.isNull(this.tariff)) {
        return false;
      }

      return true;
    },
    isCardPaymentAvailable() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      return this.isCardPaymentEnabled;
    },
    isQiwiPaymentAvailable() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isQiwiPaymentEnabled;
    },
    isYooMoneyPaymentAvailable() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isYooMoneyPaymentEnabled;
    },
    isPayMePaymentAvailable() {
      if (!this.PROJECT.paymentsEnabled) {
        return false;
      }

      if (this.isSubscription) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isPayMePaymentEnabled;
    },
    isShownPromoCode() {
      if (this.isTips) {
        return false;
      }

      if (this.$validate.isNull(this.tariff)) {
        return false;
      }

      return this.tariff.promoCodeApplicable;
    },
    displayAmountPlaceholder() {
      return this.$validate.isNull(this.promoCodeCompleted)
        ? this.$t('transaction.sum') : this.$t('discounted.sum');
    },
    displayAmountWithoutPromocode() {
      if (this.$validate.isNull(this.tariff)) {
        return '';
      }

      return formatAmount(this.tariff.amount * this.countTickets, this.currency);
    },
    getPaymentMethods() {
      return [
        this.isCardPaymentEnabled && 'bank_card',
        this.isGooglePayEnabled && 'google_pay',
        this.isApplePayEnabled && 'apple_pay',
      ].filter(i => i);
    },
    hasWidgetYooMoney() {
      if (this.isSubscription) {
        return false;
      }

      if (!this.isExternalCheckoutProvider) {
        return false;
      }

      if (this.$validate.isEmptyArray(this.getPaymentMethods)) {
        return false;
      }

      return true;
    },
    isShownPaymentButtons() {
      return this.isCardPaymentAvailable
        || this.isShownYandexPayment
        || this.isShownApplePayment
        || this.isShownGooglePayment
        || this.isQiwiPaymentAvailable
        || this.isYooMoneyPaymentAvailable
        || this.hasWidgetYooMoney
        || this.isPayMePaymentAvailable;
    },
    availableBillTariffs() {
      const { billTariffs } = this.PROJECT;

      if (this.$validate.isUndefined(billTariffs)) {
        return [];
      }

      return billTariffs.filter(tariff => {
        const { arrangement } = tariff;

        if (this.$validate.isNull(arrangement)) {
          return true;
        }

        if (this.$validate.isNull(arrangement.count)) {
          return true;
        }

        return arrangement.count > arrangement.countSold;
      });
    },
    totalAmount() {
      const amount = this.tariff.amount ? this.tariff.amount : this.formattedAmount;
      const discount = this.discountAmount;
      const count = this.countTickets;

      return this.tariff.amount
        ? (amount - discount) * count : amount - discount;
    },
    getRating() {
      if (!this.rating) {
        return null;
      }

      return {
        rating: this.rating,
        type: this.PROJECT.senderFields.rating.type,
      };
    },
    getTips() {
      if (!this.isTips) {
        return null;
      }

      if (this.$validate.isNull(this.tariff)) {
        return null;
      }

      if (this.$validate.isNull(this.tariff.amount)) {
        return null;
      }

      return {
        amount: this.tariff.amount,
        percent: calcPercent(this.formattedAmount, this.tariff.amount),
      };
    },
    isShownComissionInfo() {
      if (this.$validate.isFixedLengthArray(this.getPaymentEnabledOptions, 1)) {
        return this.getPaymentEnabledOptions[0].name !== 'QIWI';
      }

      return true;
    },
    ...mapState(['PROJECT']),
    ...mapGetters([
      'isDeviceSize',
      'isApplePayEnabled',
      'isGooglePayEnabled',
      'isCardPaymentEnabled',
      'isQiwiPaymentEnabled',
      'isYooMoneyPaymentEnabled',
      'isPayMePaymentEnabled',
      'isYandexPayEnabled',
      'isExternalCheckoutProvider',
      'isExternalRedirect',
      'getOfferta',
      'getPolicyPrivacy',
      'getAgreement',
      'getLanguage',
      'isCurrencyCrypto',
      'getPaymentEnabledOptions',
    ]),
    ...mapGetters('subscription', ['tariff', 'writeOffOption']),
  },
  watch: {
    amount: {
      immediate: false,
      // eslint-disable-next-line prefer-arrow-callback
      handler: throttle(function(val) {
        this.processingExternalCheckoutWidget(val);
      }, 500),
    },
    senderFields: {
      deep: true,
      immediate: false,
      // eslint-disable-next-line prefer-arrow-callback
      handler: throttle(function(val) {
        if (this.isValid) {
          this.processingExternalCheckoutWidget(this.amount);
          return;
        }

        this.externalCheckoutClear();
      }, 500),
    },
    promoCodeCoupon: {
      handler: throttle(async function(val) {
        try {
          const { data } = await HTTPService.getAmountWithDiscount({
            billCode: this.billCode,
            billTariffId: this.billTariff.id,
            projectId: this.PROJECT.project_id,
            promoCode: val,
          });

          this.amount = formatAmount(data.overallAmount * this.countTickets, {
            significant_zeros: this.currency.significant_zeros,
            type: this.currency.type,
          });
          this.discountAmount = data.promoCode.discountAmount;
          this.promoCodeCompleted = data.promoCode;
          this.isPromoCodeInvalid = false;
        } catch (error) {
          // logrocket.logger(error);
          console.log(error);
          this.discountAmount = 0;
          this.setAmount();
          this.promoCodeCompleted = null;
          this.isPromoCodeInvalid = this.$validate.isNotEmptyString(val);
        }
      }, 500),
    },
  },
  methods: {
    async processingExternalCheckoutWidget(amount) {
      if (!this.hasWidgetYooMoney || !this.isValid) {
        this.externalCheckoutClear();
        return;
      }

      if (this.$validate.isEmptyString(amount)) {
        this.externalCheckoutClear();
        return;
      }

      const payload = this.getPaymentCreatePayload();

      try {
        const { data } = await this.PROJECT_PAYMENT_CREATE(payload);
        const { providerConfirmationToken, providerReturnUrl } = data;

        if (providerConfirmationToken && providerReturnUrl) {
          this.externalCheckoutInitialize(providerConfirmationToken, providerReturnUrl);
        }
      } catch (error) {
        console.log(error);
        this.externalCheckoutClear();
      }
    },
    getPaymentCreatePayload(config = {}) {
      const data = Object.assign({}, config, this.getPaymentData);

      if (this.$validate.isObjectWithFields(this.promoCodeCompleted, ['id'])) {
        data.billPromoCodeId = this.promoCodeCompleted.id;
      }

      return data;
    },
    showPreview() {
      document.getElementById('previewScrollable').classList.toggle('preview-open');
    },
    setFocusToAvailableInputField() {
      setTimeout(() => {
        const focusElements = document.getElementsByClassName('focusElements');

        for (const inputWrapper of focusElements) {
          const inputField = inputWrapper.querySelectorAll('textarea, input')[0];

          if (!inputField.readOnly) {
            inputField.focus();
            return;
          }
        }
      }, 400);
    },
    initializeApplePayment() {
      this.SET_APPLE_PAY_SESSION(null);

      // eslint-disable-next-line no-undef
      this.isApplePayAvailable = window.ApplePaySession && ApplePaySession.canMakePayments();
    },
    initializeGooglePayment() {
      if (isSafari) {
        return;
      }

      try {
        if (isAndroid) {
          // eslint-disable-next-line no-undef
          this.isGooglePayAvailable = this.$validate.isNotUndefined(google) && 'PaymentRequest' in window;
        } else {
          // eslint-disable-next-line no-undef
          this.isGooglePayAvailable = this.$validate.isNotUndefined(google);
        }
      } catch (error) {
        // logrocket.logger(error);
      }
    },
    initializeYandexPayment() {
      // eslint-disable-next-line no-undef
      console.log('initialize.yandex.payment', YaPay, MERCHANT_ID);

      // eslint-disable-next-line no-undef
      this.isYandexPayAvailable = this.$validate.isNotUndefined(YaPay);
      console.log('YANDEX_PAY:isAvailable', this.isYandexPayAvailable);

      // TODO: @danealton можно удалить после окончательного тестирования
      // if (process.env.NODE_ENV === 'development') {
      //   this.isYandexPayAvailable = this.$validate.isNotUndefined(YaPay);
      //   console.log('STAGE:MODE::yandex.pay.available', this.isYandexPayAvailable);
      //   return;
      // }

      // this.isYandexPayAvailable = window.YaPay && await this.isAllowYaPayForMerchant();
      // console.log('PROD:MODE::yandex.pay.available', this.isYandexPayAvailable);
    },
    // TODO: @danealton можно удалить после окончательного тестирования
    // async isAllowYaPayForMerchant() {
    //   const merchant = {
    //     merchantId: MERCHANT_ID,
    //   };

    //   const checkResult = await YaPay.readyToPayCheck(merchant);
    //   console.log('isAllowYaPayForMerchant', checkResult);

    //   return checkResult;
    // },
    async loadBill() {
      if (!this.$validate.isObjectWithFields(this.$route.params, ['id'])) {
        this.$router.push({ name: 'service.error' });
        return;
      }

      this.loading = true;

      try {
        await this.GET_BILL(this.billCode);
        await this.GET_PROJECT_LOCALE({ billCode: this.billCode });

        if (!this.PROJECT.status || this.PROJECT.status === 'INACTIVE') {
          this.$router.push({ name: 'project.blocked' });
          return;
        }

        if (this.PROJECT.bill_status === 'CLOSED') {
          this.$router.push({ name: 'bill.payed' });
          return;
        }

        if (this.$validate.isFixedLengthArray(this.availableBillTariffs, 1)) {
          this.onSelectTariff(this.availableBillTariffs[0]);
        }

        const { tildaParams } = this.PROJECT;

        if (this.$validate.isNotEmptyObject(tildaParams)) {
          this.prepareTildaParams(tildaParams);
        }

        this.autoFillSenderFields();
      } catch (error) {
        if (error.status === 400 && error.data && error.data.status === 'NO_TICKETS_LEFT') {
          this.SET_TRANSACTION({ billId: this.billCode });
          this.SET_TRANSACTION_KEY(this.billCode);
          this.$router.push({ name: 'no.tickets.left' });
          return;
        }

        if (error.status === 403) {
          this.SAVE_BILL(error.data);
          this.$router.push({ name: 'bill.blocked' });
          return;
        }

        this.$router.push({ name: 'service.error' });
      } finally {
        this.loading = false;
      }
    },
    showField(field) {
      return this.senderFieldList.includes(field);
    },
    handleSubmitUnsafeDeal() {
      this.createPayment(this.paymentTypeByUnsafeDeal);
    },
    async goPay(event, paymentType = null) {
      if (!this.isValid) {
        this.validateForm();
        return;
      }

      if (this.useSafeDeal(paymentType)) {
        this.paymentTypeByUnsafeDeal = paymentType;
        this.isShownUnsafeDealInfo = true;
        return;
      }

      this.createPayment(paymentType);
    },
    async createPayment(paymentType) {
      this.loading = true;
      this.SET_PAYMENT_TYPE(paymentType);

      try {
        const { data } = await HTTPService.getBill(this.billCode);

        if (!data.status || data.status === 'INACTIVE') {
          this.$router.push({ name: 'project.blocked' });
          return;
        }

        if (data.bill_status === 'CLOSED') {
          this.$router.push({ name: 'bill.payed' });
          return;
        }
      } catch (error) {
        if (error.status === 400 && error.data && error.data.status === 'NO_TICKETS_LEFT') {
          this.SET_TRANSACTION({ billId: this.billCode });
          this.SET_TRANSACTION_KEY(this.billCode);
          this.$router.push({ name: 'no.tickets.left' });
          return;
        }

        if (error.status === 403) {
          this.SAVE_BILL(error.data);
          this.$router.push({ name: 'bill.blocked' });
          return;
        } else {
          this.$router.push({ name: 'service.error' });
          return;
        }
      }

      const payload = Object.assign({}, this.getPaymentData);

      if (this.$validate.isNotNull(paymentType)) {
        payload.type = paymentType;
      }

      if (this.$validate.isObjectWithFields(this.promoCodeCompleted, ['id'])) {
        payload.billPromoCodeId = this.promoCodeCompleted.id;
      }

      if (this.isCurrencyCrypto) {
        HTTPService.createCryptoPayment(payload)
          .then(response => {
            const { data } = response;
            console.log('create:crypto.payment', data);

            this.SET_TRANSACTION(data);
            this.SET_TRANSACTION_KEY(this.billCode);

            this.$router.push({
              name: 'crypto.processing',
              query: {
                paymentId: data.id,
                processToken: data.processToken,
              },
            });
          })
          .catch(() => {
            this.$router.push({ name: 'service.error' });
          })
          .finally(() => {
            this.loading = false;
          });

        return;
      }

      HTTPService.createPayment(payload)
        .then(response => {
          const { data } = response;
          console.log('create:payment', data);

          this.SET_TRANSACTION(data);
          this.SET_TRANSACTION_KEY(this.billCode);

          if (this.isExternalCheckoutProvider && this.isExternalRedirect && data.externalPayUrl) {
            window.location.assign(data.externalPayUrl);
            return;
          }

          if (data.externalPayUrl) {
            window.location.assign(data.externalPayUrl);
            return;
          }

          this.$router.push({ name: 'EnterCard' });
        })
        .catch((error) => {
          if (error.status === 400 && error.data && error.data.status === 'NO_TICKETS_LEFT') {
            this.SET_TRANSACTION({ billId: this.billCode });
            this.SET_TRANSACTION_KEY(this.billCode);
            this.$router.push({ name: 'no.tickets.left' });
            return;
          }

          this.$router.push({ name: 'service.error' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getDescription() {
      if (!this.PROJECT.bill_description) {
        return '';
      }
      if (!this.slicedDescription) {
        return this.PROJECT.bill_description;
      }
      if (this.PROJECT.bill_description.length > SLICE_LENGTH) {
        return `${this.PROJECT.bill_description.substr(0, SLICE_LENGTH)}...`;
      }
      return this.PROJECT.bill_description;
    },
    autoFillSenderFields() { // Заполняем поля и удаляем ненужные
      const { sender } = this.PROJECT;
      if (this.$validate.isNull(sender)) {
        return;
      }

      // В sender есть уникальные поля. Отключить все уникальные в senderFields
      const isUniqueFieldInSender = UNIQUE_FIELDS
        .some(field => sender.hasOwnProperty(field)); // eslint-disable-line no-prototype-builtins

      if (isUniqueFieldInSender) {
        UNIQUE_FIELDS.forEach(field => {
          this.PROJECT.senderFields[field] = false;
        });
      }

      this.senderFields = Object.assign(
        this.senderFields,
        sender
      );
    },
    async onSelectTariff(tariff) {
      await this.SET_TARIFF(tariff);

      if (this.$validate.isNotNull(tariff.subscription)) {
        this.onSelectWriteOffOption(tariff.subscription.per);
      }

      this.setAmount();
    },
    async onResetTariff() {
      this.SET_TARIFF(null);
      this.promoCodeCoupon = '';
      this.countTickets = 1;
    },
    onSelectWriteOffOption(option) {
      if ([SELECTIVELY].includes(option)) {
        this.SET_WRITE_OFF_OPTION(DAILY);
        return;
      }

      this.SET_WRITE_OFF_OPTION(option);
    },
    setAmount() {
      if (this.$validate.isNull(this.tariff)) {
        return;
      }

      if (!this.PROJECT.paymentsEnabled) {
        this.amount = `XXX ${this.currency.sign}`;
        this.readonlyAmount = true;
        return;
      }

      if (this.isTips) {
        this.readonlyAmount = false;
        return;
      }

      if (this.$validate.isNull(this.tariff.amount)) {
        this.amount = '';
        this.readonlyAmount = false;
        return;
      }

      this.amount = formatAmount(this.totalAmount, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });
      this.readonlyAmount = this.formattedAmount > 0;
    },
    handleLoading(loading) {
      this.loading = loading;
    },
    prepareTildaParams(data) {
      if (this.$validate.isObjectWithFields(data, ['amount']) && this.$validate.isNotNull(data.amount)) {
        this.amount = formatAmount(data.amount, {
          significant_zeros: this.currency.significant_zeros,
          type: this.currency.type,
        });
        this.readonlyAmount = this.formattedAmount > 0;
      }

      this.tildaParams = data;
      this.tildaParams.techFields = JSON.parse(data.techFields);
      this.tildaParams.customFields = JSON.parse(data.customFields);
      this.tildaParams.products = JSON.parse(data.products);
    },
    externalCheckoutClear() {
      if (this.hasWidgetYooMoney) {
        this.checkout = null;
        this.$refs.externalCheckoutWidget.innerHTML = '';
      }
    },
    externalCheckoutInitialize(token, returnUrl) {
      if (this.$validate.isNotNull(this.checkout)) {
        this.externalCheckoutClear();
        this.externalCheckoutInitialize(token, returnUrl);
        return;
      }

      // Инициализация виджета. Все параметры обязательные.
      this.checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: token, // Токен, который перед проведением оплаты нужно получить от ЮKassa
        return_url: returnUrl, // Ссылка на страницу завершения оплаты, это может быть любая ваша страница

        // При необходимости можно изменить цвета виджета, подробные настройки см. в документации
        customization: {
          // Выбор способа оплаты для отображения
          payment_methods: this.getPaymentMethods,
        },
        error_callback(error) {
          // Обработка ошибок инициализации
          console.error(error);
        },
      });

      // Отображение платежной формы в контейнере
      this.checkout.render('payment-form');
    },
    useSafeDeal(paymentType) {
      if (!this.PROJECT.safeDeal) {
        return false;
      }

      return UNSAFE_PAYMENT_TYPES.includes(paymentType);
    },
    handleUpdateCount(count) {
      this.countTickets = count;

      if (this.tariff.amount) {
        this.amount = this.totalAmount > 0
          ? formatAmount(this.totalAmount, {
            significant_zeros: this.currency.significant_zeros,
            type: this.currency.type,
          })
          : '';
      }
    },
    handleChangeTipsAmount(amount) {
      this.amount = formatAmount(amount, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });
    },
    async validateForm() {
      const emailInput = this.$refs.email;
      const phoneInput = this.$refs.phone;
      const amountInput = this.$refs.amount;

      if (emailInput) {
        emailInput.$v.$touch();

        if (emailInput.$v.$error) {
          emailInput.$refs.email.focus();
        }
      }

      if (phoneInput) {
        phoneInput.$v.$touch();

        if (phoneInput.$v.$error) {
          phoneInput.$refs.phone.focus();
        }
      }

      if (amountInput) {
        amountInput.$v.$touch();

        if (amountInput.$v.$error) {
          amountInput.$refs.price.focus();
        }
      }
    },
    ...mapActions([
      'GET_BILL',
      'SAVE_BILL',
      'PROJECT_PAYMENT_CREATE',
      'GET_PROJECT_LOCALE',
    ]),
    ...mapMutations([
      'SET_TRANSACTION',
      'SET_TRANSACTION_KEY',
      'SET_PROJECT',
      'SET_APPLE_PAY_SESSION',
      'SET_PAYMENT_TYPE',
    ]),
    ...mapMutations('subscription', [
      'SET_TARIFF',
      'SET_WRITE_OFF_OPTION',
    ]),
  },
};
</script>

<style lang="sass" module>
  .header
    width: 100%
    padding: 30px 0

    +respond-to('medium')
      padding: 40px 0
</style>

<style lang="sass" scoped>
  .project-header-container
    width: 100%
    flex-grow: 1

    @media (max-width: 479px)
      margin-top: -16px
      z-index: 1000

  .tariff-list
    *
      &:not(:last-child)
        margin-bottom: 10px

  .comment
    p
      font-size: 14px
      line-height: 20px
      color: #212121

    &.separate
      margin-bottom: 10px
      box-shadow: 0px 1px 2px rgba(189, 189, 189, 0.5)

    p.comment-toggler
      color: #9b51e0
      cursor: pointer

  .payment-amount
    position: relative

    &.single-block
      border-radius: 8px 8px 0 0
      overflow: hidden

    .yandex-payment-wrapper
      padding: 0
      background: transparent

    .apple-payment-wrapper
      width: 100%
      height: 50px
      padding: 0
      display: flex
      justify-content: center
      align-items: center
      border-radius: 2.48rem
      background-color: black

    .google-payment-wrapper
      padding: 0
      background: transparent

    button.qiwi-payment-button
      display: flex
      justify-content: center
      align-items: center
      font-weight: 500
      font-size: 14px
      background: #FF8C00
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #e48109

      img
        max-width: 20px !important
        max-height: 20px !important
        margin-right: 10px

    button.yoomoney-payment-button
      display: flex
      justify-content: center
      align-items: center
      font-weight: 500
      font-size: 14px
      background: #8B3FFD
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #753dca

      img
        max-width: 20px !important
        max-height: 20px !important
        margin-right: 10px

    button.payme-payment-button
      display: flex
      justify-content: center
      align-items: center
      background: #33CCCC
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #30b0b0

      img
        max-width: 83px !important
        max-height: 24px !important

    .self-employed-wrapper
      margin-top: 15px
      padding: 0

    button
      padding: 0
      height: 50px

  .amount-before-promocode
    position: absolute
    top: 22px
    right: 20px
    font-size: 18px
    line-height: 22px
    color: rgba(40, 41, 61, 0.75)
    text-decoration: line-through
    z-index: 100

  .icon-coupon-completed
    width: 24px
    height: 24px
    position: absolute
    right: 20px
    background-image: url(~@/assets/svg/circle-checked.svg)
    background-repeat: no-repeat
    background-position: center

  .icon-coupon-invalid
    width: 24px
    height: 24px
    position: absolute
    right: 20px
    background-image: url(~@/assets/svg/circle-x.svg)
    background-repeat: no-repeat
    background-position: center
    cursor: pointer

  .focusElements.icons
    padding-right: 40px

  .policy-privacy
    margin-top: 20px
    padding: 0
    font-weight: 400
    font-size: 12px
    line-height: 17px
    color: #212121

    &::v-deep
      a
        display: inline
        color: #9B51E0
        word-break: break-word

  .payment-buttons
    padding: 0
    margin-top: 20px
    background-color: transparent

    button
      margin: 0

    .payment-button:not(:last-child)
      margin-bottom: 10px
</style>
